import { useEffect, useState, useSyncExternalStore } from 'react'
import { api, shortDate, type Vendor } from '../lib/api'
import {
  EVERY_VENDOR,
  buildHeldBy,
  copyWord,
  createEvidence,
  lockedWords,
  otherScopeWords,
  packFile,
  saveFile,
  stoppedWords,
  unshownWords,
  type ChainResult,
  type CopyTry,
  type EvidenceScreen,
  type PackFile,
  type PackScope,
  type PackState,
} from '../lib/evidenceState'
import { ErrorBox } from '../components/DataTable'
import { Status, Toggletip } from '../components/kit'
import { Icon } from '../components/ui'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { VendorPicker, type Unread } from '../components/VendorPicker'
import '../styles/evidence.css'
import '../styles/sweep.css'

// What the check found, in words. The state is the first thing in the card and
// is never carried by colour: an auditor reading this on a projector, or
// through a screen reader, gets the same sentence.
export function chainWords(chain: ChainResult): { headline: string; detail: string } {
  const events = chain.events + (chain.events === 1 ? ' event' : ' events')
  if (chain.ok) return { headline: 'Chain intact, ' + events, detail: '' }
  return {
    headline: chain.breakAt ? 'Chain broken at sequence ' + chain.breakAt : 'Chain broken: the head does not match its events',
    detail: chain.breakAt ? 'The check stopped there, after reading ' + events + '.' : 'All ' + events + ' were read.',
  }
}

type Clause = { clause: string; obligation: string }

type PackPart = {
  pack: string
  source?: string
  clausesInClauseSet: number
  clausesCited: number
  cited: Clause[]
  notCited: Clause[]
  citedOutsideClauseSet: string[]
  packLevelCitations: number
}

type Coverage = {
  measuredOverQuestionnaireVersions: string[]
  basis: string
  packs: PackPart[]
  packsWithoutClauseSet: { pack: string; cited: string[]; reason: string }[]
}

// The pack is a large, deliberately shaped document. These are the parts the
// screen reads; the rest is rendered as the downloadable artefact rather than
// being flattened into a table that loses its structure.
type Pack = {
  schema?: string
  generatedAt?: string
  // What the API was asked for, in its own record of the request.
  request?: { vendorId?: string }
  vendorsInScope?: number
  vendorsIncluded?: number
  truncated?: boolean
  evidenceChain?: ChainResult
  coverage?: Coverage
  limitations?: string[]
  contentSha256?: string
  [k: string]: unknown
}

// The head hash is what an auditor holds this chain against: the figure in an
// export pack, or the one they wrote down last quarter. Sixty-four characters
// are compared by copying, not by eye.
function CopyHash({ hash }: { hash: string }) {
  // What was copied, not just that something was. The head moves every time an
  // act is recorded, and a button still reading Copied beside a head that was
  // never copied sends the old hash to whoever it is pasted for.
  const [tried, setTried] = useState<CopyTry>(null)
  const said = copyWord(tried, hash)
  return (
    <button
      type="button"
      className="error-ref-copy"
      aria-label={said === 'Copy' ? 'Copy the head hash' : said === 'Copied' ? 'Copied the head hash' : 'The head hash was not copied'}
      onClick={() => {
        // Absent outside a secure context, and refused where the browser asks
        // first and is told no. Either way the button says it did not copy.
        const copying = navigator.clipboard?.writeText(hash)
        if (!copying) return setTried({ hash, ok: false })
        void copying.then(
          () => setTried({ hash, ok: true }),
          () => setTried({ hash, ok: false }),
        )
      }}
    >
      <Icon name="copy" size={13} /> {said}
    </button>
  )
}

// Whether the chain holds, on one line, and loudly when it does not.
//
// A chain that does not verify, and a check that could not run, are each said
// in words above everything else in the card. A check that failed also takes
// the last verdict off the screen: "intact" from five minutes ago, under a
// check that has just failed, would be read as the answer.
export function ChainCard({
  chain: last,
  error,
  checking,
  onCheck,
}: {
  chain: ChainResult | null
  error: unknown
  checking: boolean
  onCheck: () => void
}) {
  // While a check is out there is no verdict to draw, whatever the last one
  // was: the tick and "intact" are an answer, and this check has not given
  // one. Nor is one drawn beside a check that could not run. The state this is
  // drawn from already holds neither; the card does not depend on that.
  const chain = checking || error != null ? null : last
  const said = chain ? chainWords(chain) : null
  return (
    <section className="panel ev-chain" aria-labelledby="ev-chain-heading" aria-busy={checking}>
      <h2 id="ev-chain-heading" className="vh">
        Chain integrity
      </h2>

      {error != null && !checking && <ErrorBox error={error} what="The chain" verb="checked" />}

      {chain && said && !chain.ok && (
        <div className="ev-chain-broken" role="alert">
          <Icon name="alert" size={20} />
          <div>
            <strong>{said.headline}</strong>
            {chain.detail && <p>{chain.detail}</p>}
            <p>{said.detail}</p>
          </div>
        </div>
      )}

      <div className="ev-chain-row">
        {chain && said && chain.ok && (
          <>
            <p className="ev-chain-state" role="status">
              <span className="ev-chain-mark" aria-hidden="true">
                <Icon name="check" size={14} />
              </span>
              <strong>{said.headline}</strong>
            </p>
            {chain.headHash && (
              <p className="ev-chain-hash">
                <span>Head hash</span>
                <span className="mono">{chain.headHash}</span>
                <CopyHash hash={chain.headHash} />
                {/* What intact means is the same every time, so it sits
                    behind the (i) beside the fact it explains. */}
                <Toggletip label="What intact means">
                  {'All ' +
                    chain.events +
                    ' events hash over their predecessor, and the head matches what the chain state records.'}
                </Toggletip>
              </p>
            )}
          </>
        )}
        {/* In the verdict's own place, and announced: role status is a
            polite live region. */}
        {!chain && (checking || error == null) && (
          <p className="ev-chain-state muted" role="status">
            Checking the chain
          </p>
        )}
        <button type="button" className="ev-chain-again" onClick={onCheck} disabled={checking}>
          {checking ? 'Checking' : 'Check again'}
        </button>
      </div>
    </section>
  )
}

// One for the life of the page, not one a visit: a pack that is building when
// the person goes to another screen is on this one when they come back.
let shared: EvidenceScreen<Pack> | null = null
function evidenceScreen(): EvidenceScreen<Pack> {
  shared ??= createEvidence<Pack>({
    verify: () => api.evidenceVerify(),
    pack: (params) => api.evidencePack(params) as Promise<{ pack: Pack; recorded: boolean }>,
    vendors: () => api.vendors({}).then((r) => r.vendors),
  })
  return shared
}

// screen is the screen's state and what changes it (lib/evidenceState.ts). A
// test hands one in, with an API it answers itself; the console uses its own.
export function Evidence({ screen = evidenceScreen() }: { screen?: EvidenceScreen<Pack> } = {}) {
  const { chain, packs, vendors, vendorsUnread } = useSyncExternalStore(screen.subscribe, screen.state, screen.state)

  // Arriving: the chain is checked afresh, the register read, and what the
  // last visit left that this one cannot see is let go.
  useEffect(() => screen.open(), [screen])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Evidence chain</h1>
        </div>
      </div>

      <ChainCard chain={chain.chain} error={chain.error} checking={chain.checking !== null} onCheck={screen.check} />

      <section className="panel" aria-labelledby="ev-pack-heading">
        <h2 id="ev-pack-heading">Export pack</h2>
        {/* Always here: a pack that was built and
            never reached this screen (the page reloaded while it built, the
            wait was given up) is still an act on the chain, and somebody
            reading the audit trail should not have to work out where it came
            from. The second sentence is the case the first is not true of:
            the API hands over a pack whose export it could not record, and
            says so (exportRecorded in lib/api.ts). That word reaches a person
            only if the answer reaches this screen: a page reloaded while the
            pack built never hears it, which is why the sentence is limited to
            the pack that arrives (both reviews of 12 October 2026). */}
        <p className="sw-help">
          Building a pack is recorded in the audit trail, whether or not the pack is downloaded. If recording fails
          and the pack reaches this screen, the screen says so.
        </p>

        <PackControls
          state={packs}
          vendors={vendors}
          unread={vendorsUnread}
          onRetry={screen.readVendors}
          onScope={screen.scope}
          onTyped={screen.typed}
          onBuild={screen.build}
          onStop={screen.stopWaiting}
          onSave={saveFile}
        />

        {/* The picker says its list could not be read, in the box's place,
            so the box no longer says what Build would be for. */}
        {vendorsUnread !== null && packs.scope === '' && packs.asking === null && (
          <p className="ev-pack-note muted">A pack for every vendor in this tenant can still be built.</p>
        )}
        <PackOutcome state={packs} />
      </section>
    </>
  )
}

// The scope, Build and Download, on one row, drawn from the state.
//
// While a pack is building the picker's place holds words saying whose (see
// lockedWords), and Build says Building. Build is not taken off the page or
// disabled outright while it waits: a disabled button drops the focus onto
// the page, and the person who pressed it with the keyboard would be put back
// at the top when the pack arrived. It says it is unavailable, and a press on
// it asks for nothing.
export function PackControls({
  state,
  vendors,
  unread = null,
  onRetry,
  onScope,
  onTyped,
  onBuild,
  onStop,
  onSave,
}: {
  state: PackState<Pack, unknown>
  vendors: Vendor[] | null
  unread?: Unread
  onRetry?: () => void
  onScope: (vendorId: string) => void
  onTyped: (text: string) => void
  onBuild: () => void
  onStop: () => void
  onSave: (file: PackFile) => void
}) {
  const built = state.built
  const building = buildHeldBy(state) === 'building'
  return (
    <div className="ev-pack-row">
      <VendorPicker
        id="pack-vendor"
        label="Scope"
        value={state.scope}
        options={vendors}
        unread={unread}
        onRetry={onRetry}
        everyone={EVERY_VENDOR}
        locked={lockedWords(state)}
        onChange={onScope}
        onTyped={onTyped}
      />
      <button
        type="button"
        className="primary"
        aria-disabled={building}
        onClick={() => {
          if (!building) onBuild()
        }}
      >
        {building ? 'Building' : 'Build the pack'}
      </button>
      {/* A build has no time limit. Past the wait there is a way out that is
          not a reload, and it says what stopping does not undo (PackOutcome). */}
      {state.asking?.slow && (
        <button
          type="button"
          onClick={(e) => {
            // This button goes when it is pressed, and the focus would go with
            // it. It is put on Build, which is beside it and is what the
            // person presses next.
            const build = e.currentTarget.previousElementSibling
            onStop()
            if (build instanceof HTMLElement) build.focus()
          }}
        >
          Stop waiting
        </button>
      )}
      {/* The API gave the pack, so it can be had. What the audit trail does
          not hold about it is said in the summary, and read out with this
          button. */}
      {built && (
        <button type="button" aria-describedby={built.recorded ? undefined : UNRECORDED_ID} onClick={() => onSave(packFile(built))}>
          Download it
        </button>
      )}
    </div>
  )
}

// What a build came to: nothing yet, a pack and whose it is, a refusal, or a
// build that was set aside. Drawn from the state alone, so what a late answer
// may put on the screen is decided in one place (lib/evidencePack.ts).
export function PackOutcome({ state }: { state: PackState<Pack, unknown> }) {
  const unshown = state.unshown && unshownWords(state.unshown)
  return (
    <>
      {/* Build was pressed over letters nobody chose a vendor for. Nothing
          was sent: the scope in force is still every vendor, and that is not
          what the box says. */}
      {state.held && (
        <p className="field-error ev-pack-note" role="alert">
          Choose a vendor from the list, or clear the box. No pack was asked for.
        </p>
      )}
      {/* A pack that was built and is not shown was still built, and the API
          says with each whether it recorded the export. These pass that on,
          so nobody finds an act in the audit trail with nothing here to
          explain it, or is told of one that is not there. */}
      {state.stopped && (
        <p className="ev-pack-note" role="status">
          {stoppedWords(state.stopped)}
        </p>
      )}
      {unshown && (
        <div className="error-box" role="alert">
          <h3>{unshown.title}</h3>
          <p>{unshown.body}</p>
        </div>
      )}
      {state.otherScope && (
        <div className="error-box" role="alert">
          <h3>The pack that came back is for another scope</h3>
          <p>{otherScopeWords(state.otherScope)}</p>
        </div>
      )}
      {state.error != null && <ErrorBox error={state.error} what="The pack" />}
      {state.built && <PackSummary pack={state.built.pack} scope={state.built.scope} recorded={state.built.recorded} />}
    </>
  )
}

// The note on a pack whose export the audit trail does not hold. Download is
// described by it.
const UNRECORDED_ID = 'pack-unrecorded'

function PackSummary({ pack, scope, recorded }: { pack: Pack; scope: PackScope; recorded: boolean }) {
  const cov = pack.coverage
  return (
    <div className="panel panel-flush">
      {/* First in the summary, above whose pack it is: a pack handed on as
          evidence is expected to have its export on the chain, and this one
          has not. */}
      {!recorded && (
        <div className="note warn ev-pack-unrecorded" role="alert" id={UNRECORDED_ID}>
          <strong>The export of this pack is not in the audit trail</strong>
          This pack was built, but its export could not be recorded in the audit trail. Build it again before it is
          relied on.
        </div>
      )}
      {/* Whose pack this is, from the scope it was asked for. The figures
          below are the same shape for one vendor and for all of them, and the
          file is what gets forwarded. */}
      <p className="ev-pack-whose" data-testid="pack-scope">
        <span className="muted">Pack for</span>
        <strong>{scope.name}</strong>
        {scope.code && <span className="mono">{scope.code}</span>}
      </p>
      <div className="stat-strip">
        <div className="stat">
          <span className="metric">{pack.vendorsIncluded ?? 0}</span>
          <span className="muted">
            vendors included{pack.vendorsInScope ? ' of ' + pack.vendorsInScope + ' in scope' : ''}
          </span>
        </div>
        <div className="stat">
          <span className="metric">{pack.evidenceChain?.ok ? 'Intact' : 'Broken'}</span>
          <span className="muted">chain at time of export</span>
        </div>
        <div className="stat">
          <span className="metric mono">{pack.contentSha256?.slice(0, 12)}</span>
          <span className="muted">content hash</span>
        </div>
      </div>

      {pack.truncated && (
        <div className="issue-list" role="alert">
          <h3>This pack is truncated</h3>
          <p className="sw-row">
            <span>It does not contain every vendor in scope</span>
            <Toggletip label="Why the pack says so">
              A pack that silently stops short is worse than no pack, so it says so here and in the limitations below.
            </Toggletip>
          </p>
        </div>
      )}

      <p className="muted">
        Generated {pack.generatedAt ? shortDate(pack.generatedAt) : 'just now'}
        {pack.schema ? ' against schema ' + pack.schema : ''}
      </p>

      {cov && (
        <>
          {/* The API's own account of how coverage was measured, word for
              word, behind the (i) beside the heading it explains. */}
          <h3 className="sw-title">
            Clause coverage
            <ReasonTip label="How coverage is measured" text={cov.basis} />
          </h3>

          {cov.packs.map((p) => {
            const pct = p.clausesInClauseSet > 0 ? Math.round((p.clausesCited / p.clausesInClauseSet) * 100) : 0
            return (
              <details key={p.pack} open={p.notCited.length > 0}>
                <summary>
                  <strong className="mono">{p.pack}</strong> {p.clausesCited} of {p.clausesInClauseSet} clauses cited
                  <Status tone={pct === 100 ? 'plain' : 'warn'}>{pct}%</Status>
                </summary>
                {p.notCited.length > 0 && (
                  <>
                    {p.notCited.map((c) => (
                      <div className="pack-clause" key={c.clause}>
                        <span className="mono">{c.clause}</span>
                        <span>{c.obligation}</span>
                      </div>
                    ))}
                  </>
                )}
                {p.citedOutsideClauseSet.length > 0 && (
                  <p className="muted">
                    Cited but not in the clause set:{' '}
                    <span className="mono">{p.citedOutsideClauseSet.join(', ')}</span>
                  </p>
                )}
              </details>
            )
          })}

          {cov.packsWithoutClauseSet.length > 0 && (
            <>
              <h4>Cited, but not measurable</h4>
              {cov.packsWithoutClauseSet.map((p) => (
                <p className="muted sw-row" key={p.pack}>
                  <span className="mono">{p.pack}</span>
                  <ReasonTip label={'Why ' + p.pack + ' is not measurable'} text={p.reason} />
                </p>
              ))}
            </>
          )}
        </>
      )}

      {pack.limitations && pack.limitations.length > 0 && (
        <>
          <h3>What this pack does not establish</h3>
          <ul className="limitations">
            {pack.limitations.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
